import type { FeatureExtra } from "./features.extra";

export const FEATURE_EXTRAS_2: Record<string, FeatureExtra> = {
  scheduling: {
    metrics: [
      { value: "+20%", label: "Higher productivity through reduced downtime" },
      { value: "-30%", label: "Less time lost on changeovers" },
      { value: "+25%", label: "Real-time transparency of task statuses" },
    ],
    stepsTitle: "A schedule that lives with production",
    stepsLead:
      "The automatic \"Production GPS\" reshapes the schedule in real time whenever equipment status or order priorities change.",
    steps: [
      {
        title: "Job Orders creation",
        text: "Automatic generation of Job Orders from Manufacturing Orders.",
      },
      {
        title: "Assigning to work centers",
        text: "Considering availability, priorities, and alternatives.",
      },
      {
        title: "Sequence optimization",
        text: "Accounting for setups, changeovers matrices, and transfer times.",
      },
      {
        title: "Production GPS",
        text: "Automatic rescheduling in case of breakdowns, downtime, or new priorities.",
      },
    ],
    roles: [
      {
        role: "Production Manager",
        items: [
          {
            title: "Full control of work centers",
            text: "All jobs and statuses in a single picture.",
          },
          {
            title: "Automatic rescheduling during breakdowns or downtime",
            text: "The system rebuilds the schedule on its own in case of accidents or stoppages.",
          },
          {
            title: "Minimal manual intervention",
            text: "The schedule adapts automatically, freeing time for management decisions.",
          },
        ],
      },
      {
        role: "Supervisor",
        items: [
          {
            title: "Clear orders for the team",
            text: "Operators see precise instructions with start and finish times.",
          },
          {
            title: "Automatic queue updates",
            text: "Any changes are instantly reflected in the live task sequence.",
          },
          {
            title: "Less chaos between shifts",
            text: "A transparent schedule reduces conflicts and misunderstandings across departments.",
          },
        ],
      },
      {
        role: "Chief Operations Officer (COO)",
        items: [
          {
            title: "Realistic delivery forecasts",
            text: "You see actual order completion times in real time.",
          },
          {
            title: "Transparent view of bottlenecks",
            text: "The system highlights exactly where production loses throughput.",
          },
          {
            title: "More accurate customer promises",
            text: "Orders are planned based on real resources and equipment status.",
          },
        ],
      },
    ],
    included: [
      "Automatic generation of Job Orders from MTS/MTO Manufacturing Orders.",
      "Detailed minute-by-minute execution schedules.",
      "Job Orders assigning across work centers with availability and alternatives in mind.",
      "Optimized Job Orders sequencing with setups, changeovers and transfer times considered.",
      "Changeover time matrixes for products and characteristics.",
      "Dynamic Job Orders rescheduling in case of downtime or repairs.",
      "\"Production GPS\" functionality — adaptive real-time rescheduling.",
      "Integration with maintenance schedules for work centers.",
      "Realistic execution timeline \"as of this hour\".",
      "Prioritization of critical and urgent orders.",
      "Transparent visualization of sequence and Job Order statuses.",
    ],
    capabilities: [
      { title: "See the schedule, not another table", text: "A visual production timeline exposes sequence, duration and conflicts at a glance.", plan: "Pro" },
      { title: "Combine work, then split it safely", text: "Group compatible jobs for a better run and separate them without losing the source demand.", plan: "Advanced" },
      { title: "Move work when a machine stops", text: "Update work centres and execution targets across many jobs at once.", plan: "Pro" },
      { title: "Operations happen in the right order", text: "The next operation cannot report output before its predecessor is ready.", plan: "Pro" },
      { title: "Progress without manual roll-ups", text: "See manufacturing-order completion directly in the working list.", plan: "Standard" },
    ],
    testimonialsTitle: "Trusted with process optimization",
    testimonials: [
      {
        quote:
          "My operators no longer wait for instructions. They see the current task on screen, even in case of a breakdown.",
        name: "Liam B.",
        role: "Production Planner",
      },
      {
        quote:
          "Thanks to optimization, we cut setups by 20%. The system shows how best to group tasks.",
        name: "Noah W.",
        role: "Process Engineer",
      },
      {
        quote:
          "Before, we lost time manually moving tasks around. Now the system automatically reschedules based on equipment status.",
        name: "Oliver S.",
        role: "Supervisor",
      },
      {
        quote:
          "The system has become our production navigator. Tasks are rescheduled automatically, and we no longer lose hours on manual replanning.",
        name: "James W.",
        role: "Supervisor",
      },
    ],
    closingTitle:
      "From chaos to control — launch factory.online scheduling that works in real time",
  },

  cost: {
    metrics: [
      { value: "-15%", label: "Deviations between planned and actual cost" },
      { value: "+25%", label: "Faster decision-making thanks to transparent data" },
      { value: "-15%", label: "Indirect losses caused by inaccurate calculations" },
    ],
    stepsTitle: "Costs without guesswork",
    stepsLead:
      "Know exactly how much production costs — with plan/actual in real time.",
    steps: [
      {
        title: "Standard costing",
        text: "The system calculates costs based on standard rates.",
      },
      {
        title: "Actual expenses collection",
        text: "Data on consumed materials and time are collected automatically.",
      },
      {
        title: "Actual costing",
        text: "Real cost per each product and order is calculated.",
      },
      {
        title: "Standard vs Actual comparison",
        text: "See variances instantly and act on them right away.",
      },
    ],
    roles: [
      {
        role: "Business Owner / CEO",
        items: [
          {
            title: "Transparent understanding of profitability",
            text: "Clear visibility of which products generate the most profit.",
          },
          {
            title: "Pricing design",
            text: "Confident pricing decisions ensuring costs are covered and margins achieved.",
          },
          {
            title: "Strategic decisions support",
            text: "Which products to grow, optimize, or phase out.",
          },
        ],
      },
      {
        role: "Production Manager (COO)",
        items: [
          {
            title: "Process efficiency control",
            text: "Identify where costs exceed plan (materials, time, or work centers).",
          },
          {
            title: "Quick response",
            text: "Adjust technology or workloads instantly.",
          },
          {
            title: "Loss reduction",
            text: "Spot bottlenecks and sources of overspending.",
          },
        ],
      },
      {
        role: "Chief Financial Officer (CFO)",
        items: [
          {
            title: "Accurate financial accounting",
            text: "Real cost per order, product, and operation.",
          },
          {
            title: "Plan vs actual analysis",
            text: "Track variances and identify root causes.",
          },
          {
            title: "Budget planning",
            text: "Use actual data to forecast and optimize purchasing.",
          },
        ],
      },
    ],
    included: [
      "Planned cost calculation by direct costs (materials, work centers, manpower).",
      "Use of standard material prices and labor rates.",
      "Actual cost calculation based on real stock prices and actual operation times.",
      "Cost accounting for finished goods and each input item.",
      "Plan vs actual comparison with variance analysis.",
      "Identification of deviations by category and item.",
      "Automated reports for management decisions.",
    ],
    capabilities: [
      { title: "Stock history follows the business date", text: "Review movements in the period they belong to, not only when someone clicked save.", plan: "Pro" },
      { title: "Make-or-buy answers without another export", text: "Use ready reports for stock movement, turnover and purchasing decisions.", plan: "Pro" },
      { title: "Purchased services reach product cost", text: "Subcontracting and service receipts complete the true cost picture.", plan: "Pro" },
    ],
    testimonialsTitle: "Trusted with their finances",
    testimonials: [
      {
        quote:
          "I now get planned and actual cost data for every order. This helps explain variances and make cost optimization decisions.",
        name: "Liam B.",
        role: "Finance Controller",
      },
      {
        quote:
          "Actual costs per operation are recorded automatically — far fewer manual calculations.",
        name: "Noah W.",
        role: "Cost Accountant",
      },
      {
        quote:
          "Costing data is available much faster than before — enabling timely reactions.",
        name: "Oliver S.",
        role: "Cost Accountant",
      },
      {
        quote:
          "Now we know where inefficiencies appear — and cut costs precisely at the source.",
        name: "James W.",
        role: "Financial Analyst",
      },
    ],
    closingTitle: "Do you know your real production cost? Verify it with factory.online",
  },

  "technical-data": {
    metrics: [
      {
        value: "-50%",
        label: "Reduced labor costs for maintaining BOMs thanks to Matrix BOMs",
      },
      { value: "-25%", label: "Fewer errors in BOMs and routings" },
      { value: "+20%", label: "Faster updates to production processes" },
    ],
    stepsTitle: "Data that holds production together",
    stepsLead:
      "A single catalog of BOMs, routings, and work centers ensures transparency and controlled changes at any moment.",
    steps: [
      {
        title: "Fill in master data",
        text: "Products, materials, packaging, operations, work centers.",
      },
      {
        title: "Enter BOMs",
        text: "Including group BOMs (one BOM replaces hundreds of variants).",
      },
      {
        title: "Build product multi-level BOMs",
        text: "The system auto-calculates multi-level assemblies.",
      },
      {
        title: "Define routings",
        text: "Set technological operations and alternative sequences.",
      },
    ],
    roles: [
      {
        role: "Business Owner / CEO",
        items: [
          {
            title: "Lower costs of maintaining technical data",
            text: "No duplication, less overhead.",
          },
          {
            title: "Unified data structure for the entire business",
            text: "All Team work with synchronized information without inconsistencies.",
          },
          {
            title: "Fast response to market changes with configurable products",
            text: "Matrix BOM enables launching new variants without delays.",
          },
        ],
      },
      {
        role: "Chief Operations Officer (COO)",
        items: [
          {
            title: "Transparent product and routing structure",
            text: "A clear view of processes reduces chaos and simplifies control.",
          },
          {
            title: "Control of alternative work centers and routings",
            text: "The system automatically accounts for available options to maintain productivity.",
          },
          {
            title: "Fast updates to the production process",
            text: "Data can be updated centrally without losing control.",
          },
        ],
      },
      {
        role: "Designer/Engineer",
        items: [
          {
            title: "Matrix BOM reduces time spent on maintaining specifications",
            text: "One matrix structure replaces hundreds of manual variants.",
          },
          {
            title: "Substitution of alternative materials without chaos",
            text: "The system tracks replacements and ensures accuracy.",
          },
          {
            title: "Data updates when designs change",
            text: "All changes are centralized and instantly available to the team.",
          },
        ],
      },
      {
        role: "Production Planner",
        items: [
          {
            title: "Automatic multi-level BOM calculation",
            text: "The system automatically generates multi-level structures for requirements planning.",
          },
          {
            title: "Data linked to manufacturing orders",
            text: "All specifications are directly integrated into Manufacturing Orders.",
          },
          {
            title: "Flexible configuration selection",
            text: "You can define the required configuration at the level of each order.",
          },
        ],
      },
      {
        role: "Storekeeper",
        items: [
          {
            title: "A single catalog for the entire team",
            text: "Everyone works in a shared environment without duplications.",
          },
          {
            title: "Transparent change management",
            text: "Notifications about new versions of BOMs and routings allow full control over transitioning orders to the latest versions.",
          },
          {
            title: "Multi-level BOM support",
            text: "Planning is based on actual links between semi-finished and finished products.",
          },
        ],
      },
    ],
    included: [
      "Product catalog with configurations, markings, and packaging variants.",
      "Material catalog (including waste, services, packaging).",
      "Matrix BOMs for configurable product families.",
      "Engineering BOM creation and material usage standards.",
      "Material substitutes with tracking.",
      "Automatic calculation of multi-level BOMs.",
      "Config selection and material substitution at the order level.",
      "Catalog of operations with time standards.",
      "Alternative routings with multiple sequences.",
      "Locations: warehouses, workshops.",
      "Work centers with alternation rules.",
      "Standardized transfer times between work centers.",
      "Maintenance schedules for work centers.",
      "Notifications about BOM/routing changes.",
      "Controlled transition of orders to new BOM/routing versions.",
    ],
    capabilities: [
      { title: "Your fields, without a custom project", text: "Add the industry or customer data each document actually needs.", plan: "Advanced" },
      { title: "Update master data in bulk", text: "Preview Excel imports before they change items, packaging or containers.", plan: "Standard" },
    ],
    testimonialsTitle: "Trusted foundations",
    testimonials: [
      {
        quote:
          "Matrix BOM allowed us to replace hundreds of BOMs with a single universal one. It cut engineering work by at least 40%.",
        name: "Liam B.",
        role: "Design Engineer",
      },
      {
        quote:
          "Now we clearly see which materials can be substituted. It saved production during supply disruptions.",
        name: "Noah W.",
        role: "Procurement & Planning Coordinator",
      },
      {
        quote:
          "Before, every BOM change took days. Now updates are instantly visible in MOs, and we decide — stick with the old version or switch to the new one.",
        name: "Oliver S.",
        role: "Production Planner",
      },
      {
        quote:
          "By applying alternative routings across lines, we keep machines evenly loaded and downtime under control.",
        name: "James W.",
        role: "Production Planner",
      },
    ],
    closingTitle:
      "Make your technical data a reliable foundation for production with factory.online",
  },
};
